import { Rows3, LayoutGrid, Sparkles } from 'lucide-react'
import type { BrowseMode } from '@/features/discovery/use-browse-mode'

interface BrowseModeToggleProps {
  mode: BrowseMode
  onChange: (next: BrowseMode) => void
}

/**
 * Three-way segmented control for switching Browse view style. Rendered next to sort + search
 * in the sticky header.
 *
 * - List        — info-dense rows (analytical "find that specific thing")
 * - Card        — uniform grid of image cards (browse-style)
 * - Inspiration — variable-size bento, just-pictures-until-hover (mood-board feel)
 */
export const BrowseModeToggle = ({ mode, onChange }: BrowseModeToggleProps) => (
  <div
    role="radiogroup"
    aria-label="Browse layout"
    className="inline-flex items-center rounded-md border bg-background p-0.5 text-sm"
  >
    <BrowseModeButton
      label="List"
      active={mode === 'list'}
      onClick={() => onChange('list')}
      icon={<Rows3 className="h-4 w-4" aria-hidden />}
    />
    <BrowseModeButton
      label="Card"
      active={mode === 'card'}
      onClick={() => onChange('card')}
      icon={<LayoutGrid className="h-4 w-4" aria-hidden />}
    />
    <BrowseModeButton
      label="Inspiration"
      active={mode === 'inspiration'}
      onClick={() => onChange('inspiration')}
      icon={<Sparkles className="h-4 w-4" aria-hidden />}
    />
  </div>
)

interface BrowseModeButtonProps {
  label: string
  active: boolean
  onClick: () => void
  icon: React.ReactNode
}

const BrowseModeButton = ({ label, active, onClick, icon }: BrowseModeButtonProps) => (
  <button
    type="button"
    role="radio"
    aria-checked={active}
    onClick={onClick}
    className={
      'inline-flex items-center gap-1.5 rounded-sm px-2.5 py-1 transition-colors ' +
      (active
        ? 'bg-primary text-primary-foreground'
        : 'text-muted-foreground hover:text-foreground')
    }
  >
    {icon}
    <span>{label}</span>
  </button>
)
